import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StickyNote, X, Copy, Trash2, Loader2, Check, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTimeET } from '../../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// TYPING-NOTES-001: `readOnly` renders the saved Search Processor notes for the Typing Processor —
// no textarea, autosave, copy or clear. Editable mode (all other roles) is unchanged.
const CallNotesScratchpad = ({ orderId, onCopyToUpdateNotes, readOnly = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [meta, setMeta] = useState({ name: null, at: null });
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [loading, setLoading] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const debounceRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const textareaRef = useRef(null);

  // Fetch existing draft when panel opens
  useEffect(() => {
    if (!isOpen || !orderId || hasFetchedRef.current) return;
    const fetchDraft = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/orders/${orderId}/call-notes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDraft(data.call_notes_draft || '');
          setMeta({ name: data.call_notes_updated_by_name || null, at: data.call_notes_updated_at || null });
        }
      } catch (err) {
        console.error('Failed to fetch call notes:', err);
      } finally {
        setLoading(false);
        hasFetchedRef.current = true;
      }
    };
    fetchDraft();
  }, [isOpen, orderId]);

  // Autosave with debounce
  const autosave = useCallback(
    (text) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSaveStatus('saving');
      debounceRef.current = setTimeout(async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API}/orders/${orderId}/call-notes`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ call_notes_draft: text }),
          });
          if (res.ok) {
            setSaveStatus('saved');
          } else {
            setSaveStatus('error');
          }
        } catch {
          setSaveStatus('error');
        }
      }, 1000);
    },
    [orderId]
  );

  // Reset saved indicator after 2s
  useEffect(() => {
    if (saveStatus !== 'saved') return;
    const t = setTimeout(() => setSaveStatus('idle'), 2000);
    return () => clearTimeout(t);
  }, [saveStatus]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setDraft(val);
    autosave(val);
  };

  const handleCopyToUpdateNotes = () => {
    if (!draft.trim()) {
      toast.info('Nothing to copy — scratchpad is empty');
      return;
    }
    if (onCopyToUpdateNotes) {
      onCopyToUpdateNotes(draft);
      toast.success('Copied to update notes');
    }
  };

  const handleClear = () => {
    setDraft('');
    autosave('');
    setShowClearConfirm(false);
    toast.success('Scratchpad cleared');
    textareaRef.current?.focus();
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  // Save status indicator
  const renderSaveStatus = () => {
    if (saveStatus === 'saving') {
      return (
        <span className="flex items-center gap-1 text-[11px] text-slate-400" data-testid="call-notes-status-saving">
          <Loader2 className="w-3 h-3 animate-spin" />
          Saving...
        </span>
      );
    }
    if (saveStatus === 'saved') {
      return (
        <span className="flex items-center gap-1 text-[11px] text-emerald-500" data-testid="call-notes-status-saved">
          <Check className="w-3 h-3" />
          Saved
        </span>
      );
    }
    if (saveStatus === 'error') {
      return (
        <span className="text-[11px] text-red-400" data-testid="call-notes-status-error">Save failed</span>
      );
    }
    return null;
  };

  return (
    <>
      {/* Toggle tab — always visible on right edge */}
      {!isOpen && (
        <button
          onClick={handleToggle}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-30 flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-white pl-3 pr-2 py-3 rounded-l-lg shadow-lg transition-colors"
          data-testid="call-notes-toggle-btn"
          title="Open Call Notes"
        >
          <StickyNote className="w-4 h-4" />
          <span className="text-xs font-medium writing-mode-vertical" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            Notes
          </span>
        </button>
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 z-40 h-screen transition-transform duration-200 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: 320 }}
        data-testid="call-notes-panel"
      >
        <div
          className="flex flex-col bg-white border-l border-slate-200 shadow-[-4px_0_16px_rgba(0,0,0,0.06)]"
          style={{ height: 'calc(100vh - 56px)', marginTop: 56 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/80 flex-shrink-0">
            <div className="flex items-center gap-2">
              <StickyNote className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-800" data-testid="call-notes-title">{readOnly ? 'Search notes (read-only)' : 'Call Notes'}</h3>
            </div>
            <div className="flex items-center gap-2">
              {!readOnly && renderSaveStatus()}
              <button
                onClick={handleToggle}
                className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="call-notes-close-btn"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden px-4 pt-3 pb-2">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : readOnly ? (
              draft.trim() ? (
                <div
                  className="w-full h-full overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5 text-sm text-slate-700 whitespace-pre-wrap"
                  data-testid="call-notes-readonly-text"
                >
                  {draft}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-slate-400 text-center px-4" data-testid="call-notes-readonly-empty">
                  No notes were left by the Search Processor.
                </div>
              )
            ) : (
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={handleChange}
                placeholder="Use this scratchpad during calls. Notes are auto-saved and private to this order."
                className="w-full h-full resize-none rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-colors"
                data-testid="call-notes-textarea"
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 px-4 py-3 border-t border-slate-100 bg-slate-50/50 space-y-2">
            {readOnly ? (
              <div className="text-[11px] text-slate-500" data-testid="call-notes-readonly-meta">
                {meta.at ? `Last updated by ${meta.name || 'Unknown'} · ${formatDateTimeET(meta.at)}` : 'Read-only view'}
              </div>
            ) : showClearConfirm ? (
              <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="call-notes-clear-confirm">
                <span className="text-xs text-red-700">Clear all notes?</span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handleClear}
                    className="px-2.5 py-1 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded transition-colors"
                    data-testid="call-notes-clear-confirm-yes"
                  >
                    Yes, clear
                  </button>
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="px-2.5 py-1 text-xs font-medium text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 rounded transition-colors"
                    data-testid="call-notes-clear-confirm-no"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyToUpdateNotes}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                  data-testid="call-notes-copy-btn"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy to Update Notes
                </button>
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-slate-500 hover:text-red-600 bg-white hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-lg transition-colors"
                  data-testid="call-notes-clear-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CallNotesScratchpad;
